import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { ArticleJsonLd } from '../../components/seo/SchemaMarkup';
import { SeriesNavigation } from '../../components/articles/SeriesNavigation';
import { AIRecommendations } from '../../components/articles/AIRecommendations';
import { RelatedArticles } from '../../components/articles/RelatedArticles';
import { ArticleFeedbackWidget } from '../../components/articles/ArticleFeedbackWidget';
import { CentrStickyPopup } from '../../components/CentrAffiliate';
import { PeptiqInlineCTA } from '../../components/PeptiqInlineCTA';
import AprProductGrid from '../../components/apr/AprProductGrid';
import AprStickyRail from '../../components/apr/AprStickyRail';
import { sanitizeHtml } from '../../lib/sanitize';
import TenXProductCards from '../../components/Articles/TenXProductCards';
import BloodTestPopup from '../../components/Articles/BloodTestPopup';
import { EvidenceOrganizations } from '../../components/articles/ArticleSEOComponents';
import { useStandaloneInteractivity } from './useStandaloneInteractivity';
import { ArticlePartnerOffers } from '../../components/partners/ArticlePartnerOffers';
import { BiminiAwarenessPopup } from '../../components/partners/BiminiAwarenessPopup';

const isHyroxTagged = (article) =>
  article.category === 'HYROX' || (article.tags && article.tags.some((t) => ['hyrox'].includes(t.toLowerCase())));

/**
 * Renders articles whose `layout === 'standalone'` — these ship as raw HTML
 * via `dangerouslySetInnerHTML` and rely on `useStandaloneInteractivity` for
 * tabs/FAQ/scroll-reveal/inline-script support.
 */
export const StandaloneArticleView = ({ article, slug, standaloneRef }) => {
  const [showCentrPopup, setShowCentrPopup] = useState(false);
  useStandaloneInteractivity(article, standaloneRef);
  // Memoized: React 19 re-sets dangerouslySetInnerHTML on every re-render if
  // the {__html} object identity changes, wiping DOM state (form input, etc).
  const htmlObj = useMemo(() => ({ __html: sanitizeHtml(article.content) }), [article.content]);

  return (
    <main className="pt-20 min-h-screen" data-testid="article-page">
      <ArticleJsonLd article={article} slug={slug} />
      <div
        ref={standaloneRef}
        className="standalone-article-root"
        dangerouslySetInnerHTML={htmlObj}
      />
      {slug === 'blood-tests-before-peptides-the-complete-pre-protocol-lab-guide' && (
        <div className="max-w-5xl mx-auto px-6">
          <TenXProductCards />
        </div>
      )}
      {article.category === 'Peptides' && (
        <div className="max-w-5xl mx-auto px-6">
          <AprProductGrid matchText={`${article.title || ''} ${slug || ''} ${(article.tags || []).join(' ')}`} />
          <PeptiqInlineCTA />
        </div>
      )}
      {article.category === 'Peptides' && <AprStickyRail article={article} />}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <ArticlePartnerOffers article={article} placement="article-end" />
        <EvidenceOrganizations sources={article.evidence_sources} />
        <ArticleFeedbackWidget slug={slug} />
        <SeriesNavigation currentSlug={slug} />
        <AIRecommendations currentSlug={slug} category={article.category} />
        <RelatedArticles currentSlug={slug} category={article.category} internalLinks={article.internal_links} />
      </div>
      {isHyroxTagged(article) && !showCentrPopup && (
        <div className="fixed bottom-4 right-4 z-40">
          <button
            onClick={() => setShowCentrPopup(true)}
            className="bg-[#e8ff00] text-black px-4 py-2.5 text-xs font-heading uppercase tracking-wider shadow-lg hover:opacity-90 transition-opacity flex items-center gap-2"
            data-testid="centr-popup-trigger"
          >
            <ShoppingBag className="w-3.5 h-3.5" /> HYROX Gear — 10% Off
          </button>
        </div>
      )}
      {showCentrPopup && <CentrStickyPopup onClose={() => setShowCentrPopup(false)} />}
      {slug === 'blood-tests-before-peptides-the-complete-pre-protocol-lab-guide' && (
        <BloodTestPopup articleSlug={slug} />
      )}
      {article.category !== 'HYROX' && article.category !== 'Peptides' && !['echo_offer', 'none'].includes(article.popup_config?.type) && (
        <BiminiAwarenessPopup contextId={slug} />
      )}
    </main>
  );
};
